import { PartnerEntryPoint } from '@/config/constants';
import { isBank } from '@/config/core';
import { isFood } from '@/utils/saas';

export { getSuffix } from './getSuffix';

export const COUNTRIES = process.env.COUNTRIES as unknown as { [key: string]: string };
const BASE_DOMAIN_ALL = process.env.BASE_DOMAIN_ALL as unknown as { [key: string]: string };
const LANGUAGES = process.env.LANGUAGES as unknown as {
  [key: string]: { transify: string; key: string; momentKey: string };
};

const BANK_DEFAULT_AVATAR_MAP = {
  [COUNTRIES.ID]: 'bank_id.png',
  [COUNTRIES.SG]: 'bank_sg_default_avatar.jpg',
};

const BANK_DEFAULT_AGENT_AVATAR_MAP = { [COUNTRIES.ID]: 'bank_id' };


function generateCountry() {
  let country = 'sg';

  if (typeof COUNTRY !== 'undefined') {
    country = COUNTRY;
  } else if (typeof process !== 'undefined' && typeof process.env === 'object' && process.env.country) {
    country = process.env.country;
  }

  if (process.env.NODE_ENV !== 'production') {
    const [urlMatchCountry] = location?.hostname?.match?.(new RegExp(`(${Object.values(COUNTRIES).join('|')})$`)) || [];
    country = urlMatchCountry || country;
  }

  return country;
}

function generateOfflineForm(env?: string) {
  return Object.values(COUNTRIES).reduce((prev: { [key: string]: string }, cur) => {
    prev[cur] = `https://help.${env ? `${env}.` : ''}${BASE_DOMAIN_ALL[cur]}/portal`;
    return prev;
  }, {});
}

function generateAvatarMap(type: string) {
  return Object.values(COUNTRIES).reduce(
    (prev: { [key: string]: string }, cur) => {
      prev[
        cur
      ] = isBank() && type === 'bot'
        ? require(`@/assets/img/${type}Avatar/${BANK_DEFAULT_AVATAR_MAP[COUNTRY] || BANK_DEFAULT_AVATAR_MAP[COUNTRIES.ID]}?inline`)
        : isBank() ? require(`@/assets/img/${type}Avatar/${BANK_DEFAULT_AGENT_AVATAR_MAP[cur] || cur}.png?inline`) : require(`@/assets/img/${type}Avatar/${cur}.png?inline`);
      return prev;
    },
    {
      default: isBank() && type === 'bot'
        ? `@/assets/img/${type}Avatar/${BANK_DEFAULT_AVATAR_MAP[COUNTRY] || BANK_DEFAULT_AVATAR_MAP[COUNTRIES.ID]}?inline`
        : `@/assets/img/${type}Avatar/default.png?inline`,
    },
  );
}

export const COUNTRY = generateCountry();
export const BACKUP_LOCALE = 'en';
export const LANG = (LANGUAGES[COUNTRY] && LANGUAGES[COUNTRY].key) || BACKUP_LOCALE;
export const MOMENT_LANG = (LANGUAGES[COUNTRY] && LANGUAGES[COUNTRY].momentKey) || BACKUP_LOCALE;

const LIVE_OFFLINE_FORM_URL_INHOUSE: { [key: string]: string } = {
  pl: 'https://shopee-help.force.com/pl/s/contactusform',
  es: 'https://shopee-help.force.com/es/s/contactusform',
  fr: 'https://shopee-help.force.com/fr/s/contactusform',
  in: 'https://help.shopee.in/portal/webform/19c090e43e014e24be276dc9fbc7dc93',
};

export const BANK_POLICY_URL_FOR_UAT_PH = 'https://m-uat1.uat.seabank.ph/contract/0002';
export const BANK_POLICY_URL_FOR_LIVE_PH = 'https://m.seabank.ph/contract/0002';
export const BANK_POLICY_URL_FOR_UAT = { 'ph': BANK_POLICY_URL_FOR_UAT_PH };
export const BANK_POLICY_URL_FOR_LIVE = { 'ph': BANK_POLICY_URL_FOR_LIVE_PH };

// 当没有在线客服时，需要跳转到问题表格，供客服人员追溯问题
export const LIVE_OFFLINE_FORM_URL_FOR_LIVE = {
  ...generateOfflineForm(),
  ...LIVE_OFFLINE_FORM_URL_INHOUSE,
};
export const LIVE_OFFLINE_FORM_URL_FOR_TEST = {
  ...generateOfflineForm('test'),
  ...LIVE_OFFLINE_FORM_URL_INHOUSE,
};
export const LIVE_OFFLINE_FORM_URL_FOR_UAT = {
  ...generateOfflineForm('uat'),
  ...LIVE_OFFLINE_FORM_URL_INHOUSE,
};

export const EnvEnum = {
  DEV: 'development',
  TEST: 'test',
  UAT: 'uat',
  STAGING: 'staging',
  LIVE: 'live',
};

export const BANK_POLICY_URL = {
  [EnvEnum.DEV]: BANK_POLICY_URL_FOR_UAT,
  [EnvEnum.TEST]: BANK_POLICY_URL_FOR_UAT,
  [EnvEnum.UAT]: BANK_POLICY_URL_FOR_UAT,
  [EnvEnum.LIVE]: BANK_POLICY_URL_FOR_LIVE,
}[ENVIRONMENT] || BANK_POLICY_URL_FOR_LIVE;

// todo EnvEnum.TEST的链接要修改回来
export const LIVE_OFFLINE_FORM_URL
  = {
    [EnvEnum.DEV]: LIVE_OFFLINE_FORM_URL_FOR_TEST,
    [EnvEnum.TEST]: LIVE_OFFLINE_FORM_URL_FOR_TEST,
    [EnvEnum.UAT]: LIVE_OFFLINE_FORM_URL_FOR_UAT,
    [EnvEnum.STAGING]: LIVE_OFFLINE_FORM_URL_FOR_UAT,
    [EnvEnum.LIVE]: LIVE_OFFLINE_FORM_URL_FOR_LIVE,
  }[ENVIRONMENT] || LIVE_OFFLINE_FORM_URL_FOR_LIVE;

const AGENT_AVATAR_MAP = generateAvatarMap('agent');
export const AGENT_AVATAR = AGENT_AVATAR_MAP[COUNTRY] || AGENT_AVATAR_MAP.default;

const BOT_AVATAR_MAP = generateAvatarMap('bot');
// food 下不要bot头像保持和agent头像
export const BOT_AVATAR = isFood() ? AGENT_AVATAR : (BOT_AVATAR_MAP[COUNTRY] || BOT_AVATAR_MAP.default);

export enum WEB_FORM_TYPE {
  Normal,
  Premium,
}

export enum WEB_FORM_AUTOFILL_FIELD {
  userType = 'userType',
  enquiryType = 'enquiryType',
  problemType = 'problemType',
  description = 'description',
  attachments = 'attachments',
  orderSN = 'orderSN',
}

export enum WEB_FORM_ENTRY_POINT {
  chatbot = 2,
}

export const PARTNER_OFFLINE_FROM_URL = {
  [COUNTRIES.TH]: {
    [EnvEnum.LIVE]: {
      [PartnerEntryPoint.ShopeeFoodPartner]: {
        url: 'https://help.shopee.co.th/portal/webform/0c9cdefda57e4a9ebbab13cbbedccb53',
        fieldIdMap: {
          [WEB_FORM_AUTOFILL_FIELD.userType]: '',
          [WEB_FORM_AUTOFILL_FIELD.enquiryType]: '',
        },
      },
    },
  },
  [COUNTRIES.VN]: {
    [EnvEnum.LIVE]: {
      [PartnerEntryPoint.MystockPartner]: {
        url: 'https://help.shopee.vn/portal/webform/576c220926d5444198eb633007ca907c',
        fieldIdMap: {
          [WEB_FORM_AUTOFILL_FIELD.userType]: '',
          [WEB_FORM_AUTOFILL_FIELD.enquiryType]: '',
        },
      },
    },
  },
  [COUNTRIES.MY]: {
    [EnvEnum.LIVE]: {
      [PartnerEntryPoint.ShopeePayPartner]: {
        url: 'https://help.shopee.com.my/portal/webform/a5279b470fcc4f33aeac96b96fda24a5',
        fieldIdMap: {
          [WEB_FORM_AUTOFILL_FIELD.userType]: '',
          [WEB_FORM_AUTOFILL_FIELD.enquiryType]: '',
        },
      },
      [PartnerEntryPoint.ShopeeFoodPartner]: {
        url: 'https://help.shopee.com.my/portal/webform/f71cf7f4a3fd4b21b2576a6372c9d89a',
        fieldIdMap: {
          [WEB_FORM_AUTOFILL_FIELD.userType]: '',
          [WEB_FORM_AUTOFILL_FIELD.enquiryType]: '',
        },
      },
    },
  },
  [COUNTRIES.SG]: {
    [EnvEnum.LIVE]: {
      [PartnerEntryPoint.ShopeePayPartner]: {
        url: 'https://help.shopee.sg/portal/webform/b54b08e7da234efba350a62bf8f54721',
        fieldIdMap: {
          [WEB_FORM_AUTOFILL_FIELD.userType]: '',
          [WEB_FORM_AUTOFILL_FIELD.enquiryType]: '',
        },
      },
    },
  },
}[COUNTRY]?.[ENVIRONMENT] || {};


export const BANK_OFFLINE_FORM_URL = {
  [COUNTRIES.ID]: {
    [EnvEnum.UAT]: {
      url: 'https://cs.uat.seabank.co.id/portal/webform/1b80c8bf4ec84b74a2ce17eba9723fd9',
      fieldIdMap: {
        [WEB_FORM_AUTOFILL_FIELD.userType]: '',
        [WEB_FORM_AUTOFILL_FIELD.enquiryType]: '',
      },
    },
    [EnvEnum.LIVE]: {
      url: 'https://cs.seabank.co.id/portal/webform/eda2a9cbe14d417b873431f96cef6a54',
      fieldIdMap: {
        [WEB_FORM_AUTOFILL_FIELD.userType]: '',
        [WEB_FORM_AUTOFILL_FIELD.enquiryType]: '',
      },
    },
  },
}[COUNTRY]?.[ENVIRONMENT] || {};

export const FOOD_OFFLINE_FROM_URL = {
  [COUNTRIES.VN]: {
    [EnvEnum.UAT]: {
      url: 'https://help.cs.uat.shopeefood.vn/portal/webform/9e500f7d067b4c08bb194a92f877b9b8',
      fieldIdMap: {
        [WEB_FORM_AUTOFILL_FIELD.userType]: '',
        [WEB_FORM_AUTOFILL_FIELD.enquiryType]: '',
      },
    },
    [EnvEnum.LIVE]: {
      url: 'https://help.cs.shopeefood.vn/portal/webform/2caeccd017264b68978b2eca5c47d4e4',
      fieldIdMap: {
        [WEB_FORM_AUTOFILL_FIELD.userType]: '',
        [WEB_FORM_AUTOFILL_FIELD.enquiryType]: '',
      },
    },
  },
  [COUNTRIES.TH]: {
    [EnvEnum.TEST]: { url: 'https://help.shopee.co.th/portal/webform/b4b9e9e7cffe4f549c62161419eab37b' },
    [EnvEnum.UAT]: { url: 'https://help.shopee.co.th/portal/webform/b4b9e9e7cffe4f549c62161419eab37b' },
    [EnvEnum.LIVE]: { url: 'https://help.shopee.co.th/portal/webform/b4b9e9e7cffe4f549c62161419eab37b' },
  },
}[COUNTRY]?.[ENVIRONMENT] || {};

export const getHelpHost = () => {
  const region = COUNTRY;
  const env = ENVIRONMENT;
  return `https://help.${env && env !== 'live' ? `${env}.` : ''}${BASE_DOMAIN_ALL[region]}`;
};


